import React, { FC, ReactNode, ComponentProps, CSSProperties } from 'react';
import styled, { css } from 'styled-components';

import { Spinner } from '../../Spinner';
import { Icon } from '../../Icon';
import { SubRowToggle } from './SubRowToggle';
import { TableAction } from './TableAction';
import { Checkbox, CheckboxProps } from '../../Checkbox';
import { useExpandableRow, useSelectionRow, SelectedRowProps } from './hooks';

export interface DataItem {
  [key: string]: any;
  key?: string | number;
}

type OptSubRowToggleProps = Omit<
  ComponentProps<typeof SubRowToggle>,
  'isExpanded' | 'handleToggle'
>;

interface ConfigurationRenderCellOpt {
  TableAction: typeof TableAction;
  SubRowToggle: FC<OptSubRowToggleProps>;
  isExpandedRow: boolean;
  Selection: FC<CheckboxProps>;
  isSelectedRow: boolean;
}

interface ConfigurationItem {
  columnName?: ReactNode;
  /**
   * Property name of the data object you provided in `data` array
   * to render a cell to access the data automatically.
   */
  dataKey?: string;
  isWrapping?: boolean;
  renderCell?: (
    cellData: DataItem,
    cellOpt: ConfigurationRenderCellOpt,
  ) => ReactNode;
  isSorter?: boolean;
  width?: string;
  // aligns only for cells
  style?: CSSProperties;
}

interface SortFieldProps {
  /*
   * provide sorted dataKey/desc to display the field header accordingly
   * without providing `onSort`, sorting mechanism will be ignore
   */
  sorted?: {
    dataKey?: string;
    desc?: boolean;
  };
  onSort?: (dataKey: string) => void;
  isAllExpanded?: boolean;
  toggleAllExpandable?: () => void;
  isAllSelected?: boolean;
  toggleAllSelection?: () => void;
}

interface TableDeprecatedProps extends SortFieldProps {
  testId?: string;
  /**
   * Data for table to display.
   */
  data: DataItem[];
  /**
   * Defines table data displaying per column.
   * Contains a key to access data property in column cell.
   * Contains column header and provides render function for cell.
   */
  configuration: ConfigurationItem[];
  /**
   * !!! ATTENTION !!!
   * Does not have approved look&feel so not in use at the moment.
   * !!! ATTENTION !!!
   *
   * Table header/name/title (as you name it).
   */
  title?: ReactNode;
  isLoading?: boolean;
  cellStyle?: CSSProperties;
  renderSubRow?: (item?: DataItem) => ReactNode;
  defaultExpandedRow?: boolean;
  onSelect?: (selectedRows?: SelectedRowProps[]) => void;
  selectedRowKeys?: Array<string | number>;
}

enum SortOrder {
  Asc = 'asc',
  Desc = 'desc',
  None = 'none',
}

type IconProps = ComponentProps<typeof Icon>;
interface SortIcons {
  [key: string]: IconProps['name'];
}

const sortIcon: SortIcons = {
  [SortOrder.Asc]: 'arrowDown',
  [SortOrder.Desc]: 'arrowUp',
  [SortOrder.None]: 'sort',
};

const renderTableHeader = ({
  sorted = {},
  onSort,
  isAllExpanded,
  toggleAllExpandable,
  isAllSelected,
  toggleAllSelection,
}: SortFieldProps) => (config: ConfigurationItem, index: number) => {
  const { columnName, dataKey, isSorter, width, renderCell } = config;
  const key = `table-column-header-${index}`;

  // Render SubRowToggle for the column
  const hasSubRowToggle = renderCell?.toString()?.includes('SubRowToggle');
  if (hasSubRowToggle) {
    return (
      <TableColumnHeader key={key} width={width}>
        <SubRowToggle
          isExpanded={isAllExpanded}
          handleToggle={toggleAllExpandable}
        />
      </TableColumnHeader>
    );
  }

  const hasSelection = renderCell?.toString()?.includes('Selection');
  if (hasSelection) {
    return (
      <TableColumnHeader key={key} width={width}>
        <Checkbox checked={isAllSelected} handler={toggleAllSelection} />
      </TableColumnHeader>
    );
  }

  // Should render sorter when having `onSort` and configItem isSorter & having dataKey
  if (onSort && isSorter && dataKey) {
    const onClick = () => {
      onSort(dataKey);
    };
    const isTargetSorter = sorted.dataKey === dataKey;
    const sortIconType = isTargetSorter
      ? sorted.desc
        ? SortOrder.Desc
        : SortOrder.Asc
      : SortOrder.None;
    const sortIconName = sortIcon[sortIconType];

    return (
      <TableColumnHeaderSorter key={key} width={width} onClick={onClick}>
        {columnName}
        <SortIcon name={sortIconName} size="small" />
      </TableColumnHeaderSorter>
    );
  }

  // Render normal header if not a sorter type
  return (
    <TableColumnHeader key={key} width={width}>
      {columnName}
    </TableColumnHeader>
  );
};

interface TableDataRowProps {
  rowIndex: number;
  item: DataItem;
  testId: TableDeprecatedProps['testId'];
  configuration: TableDeprecatedProps['configuration'];
  cellStyle: TableDeprecatedProps['cellStyle'];
  renderSubRow: TableDeprecatedProps['renderSubRow'];
  isExpanded: boolean;
  toggleExpandable: () => void;
  isSelected: boolean;
  toggleSelection: () => void;
}

const TableDataRow: FC<TableDataRowProps> = ({
  testId,
  rowIndex,
  item,
  configuration,
  cellStyle,
  renderSubRow,
  isExpanded = false,
  toggleExpandable,
  isSelected = false,
  toggleSelection,
}) => {
  const rowKey = `table-row-${rowIndex}`;

  return (
    <>
      <TableBodyRow
        data-test-id={`${testId}-${rowKey}`}
        isExpanded={isExpanded}
      >
        {configuration.map(
          ({ dataKey, isWrapping, renderCell, style }, cellIndex) => {
            const cellKey = `table-cell-${rowIndex}-${cellIndex}`;
            const dataByKey = dataKey ? item[dataKey] : null;

            const result = renderCell
              ? renderCell(item, {
                  TableAction,
                  SubRowToggle: (props: OptSubRowToggleProps) => (
                    <SubRowToggle
                      isExpanded={isExpanded}
                      handleToggle={toggleExpandable}
                      {...props}
                    />
                  ),
                  isExpandedRow: isExpanded,
                  Selection: (props: CheckboxProps) => (
                    <Checkbox
                      disabled={item?.isNotSelectable}
                      checked={isSelected}
                      onClick={toggleSelection}
                      {...props}
                    />
                  ),
                  isSelectedRow: isSelected,
                })
              : dataByKey;

            return (
              <TableCell
                key={cellKey}
                data-test-id={`${testId}-${cellKey}`}
                isWrapping={isWrapping}
                style={{
                  ...cellStyle,
                  ...style,
                }}
              >
                {result}
              </TableCell>
            );
          },
        )}
      </TableBodyRow>
      {isExpanded && (
        <TableSubRow data-test-id={`${testId}-${rowKey}-subrow`}>
          <TableSubRowCell
            data-test-id={`${testId}-${rowKey}-subrow-cell`}
            colSpan={configuration.length}
          >
            {renderSubRow(item)}
          </TableSubRowCell>
        </TableSubRow>
      )}
    </>
  );
};

export const TableDeprecated: FC<TableDeprecatedProps> = ({
  title,
  configuration = [],
  data = [],
  isLoading,
  testId,
  sorted,
  onSort,
  cellStyle,
  renderSubRow,
  defaultExpandedRow = false,
  onSelect,
  selectedRowKeys,
}) => {
  const tableTestId = `${testId ? `${testId}-table` : 'table'}`;

  const {
    isExpandedRow,
    isAllExpanded,
    toggleExpandedRow,
    toggleAllExpandable,
  } = useExpandableRow(data, renderSubRow, defaultExpandedRow);

  const {
    isSelectedRow,
    isAllSelected,
    toggleSelectedRow,
    toggleAllSelection,
  } = useSelectionRow(data, onSelect, selectedRowKeys);

  if (isLoading) {
    return <Spinner isCentered />;
  }

  const sortInfo: SortFieldProps = {
    sorted,
    onSort,
    isAllExpanded,
    toggleAllExpandable,
    isAllSelected,
    toggleAllSelection,
  };

  return (
    <TableWrapper data-test-id={tableTestId}>
      <TableMain>
        <TableHead>
          {title ? (
            <TableRow>
              <TableTitle colSpan={configuration.length}>{title}</TableTitle>
            </TableRow>
          ) : null}
          <TableRow>{configuration.map(renderTableHeader(sortInfo))}</TableRow>
        </TableHead>
        <TableBody>
          {data.map((item, rowIndex) => (
            <TableDataRow
              key={item.key ?? rowIndex}
              rowIndex={rowIndex}
              testId={testId}
              item={item}
              configuration={configuration}
              cellStyle={cellStyle}
              renderSubRow={renderSubRow}
              isExpanded={isExpandedRow(rowIndex)}
              toggleExpandable={toggleExpandedRow(rowIndex)}
              isSelected={isSelectedRow(item.key ?? rowIndex)}
              toggleSelection={toggleSelectedRow(item.key ?? rowIndex)}
            />
          ))}
        </TableBody>
      </TableMain>
    </TableWrapper>
  );
};

const handleColumnWidth = ({ width }: { width: ConfigurationItem['width'] }) =>
  width && `width: ${width};`;

const firstChildLeftPadding = css`
  &:first-child {
    padding-left: 15px;
  }
`;

const TableWrapper = styled.div`
  max-width: 100%;
  overflow-x: auto;
  /*
    Hack to hide horizontal scroll bar if using mouse 
    and enough space for the whole table to show
  */
  padding: 1px;
`;

const TableMain = styled.table`
  border-collapse: collapse;
  width: 100%;
`;

const TableHead = styled.thead``;

const TableRow = styled.tr``;

const TableTitle = styled.th``;

const TableColumnHeader = styled.th`
  text-align: left;
  text-transform: uppercase;
  font-size: 12px;
  color: #bebebe;
  font-weight: 500;
  padding: 5px 15px 5px 0;

  ${handleColumnWidth}
  ${firstChildLeftPadding};
`;

const TableColumnHeaderSorter = styled(TableColumnHeader)`
  cursor: pointer;
  white-space: nowrap;
`;

const TableBody = styled.tbody``;

const TableBodyRow = styled(TableRow)<{
  isExpanded?: boolean;
}>`
  background-color: white;
  border: 1px solid rgba(151, 151, 151, 0.2);

  &:first-child {
    border-top: none;
  }
  ${({ isExpanded }) =>
    isExpanded &&
    css`
      border-bottom: none;
    `}
`;

const TableCell = styled.td<{
  isWrapping?: boolean;
}>`
  white-space: ${({ isWrapping }) => (isWrapping ? 'normal' : 'nowrap')};
  padding: 10px 15px 10px 0;
  ${firstChildLeftPadding}
`;

const SortIcon = styled(Icon)`
  margin-left: 0.3rem;
  /* width: 1rem;
  height: 1rem; */
  color: #0078bd;
  vertical-align: middle;
`;

const TableSubRow = styled(TableBodyRow)`
  border-top: none;
`;

const TableSubRowCell = styled.td``;
